// @ts-strict-ignore
import { inject, Injectable } from '@angular/core';
import { Store } from '@fc-core/store-utils/store';
import {
  VehicleNote,
  VehicleNoteForm,
} from '@fc-vehicles/vehicle-notes/models/vehicle-note.model';
import { VehicleNoteApiService } from '../services/api/vehicle-note-api.service';
import { NoteParams } from '@fc-projects/project-notes/models/note-params';

export interface VehicleNoteStoreState {
  notes: VehicleNote[];
  pinned: VehicleNote[];
  loading: boolean;
  listLoading: boolean;
  loadMoreLoading: boolean;
  error: any;
  offset: string | null;
  loaded: boolean;
  editNoteId: number | null;
}

const initialState: VehicleNoteStoreState = {
  notes: [],
  pinned: [],
  loading: false,
  listLoading: false,
  loadMoreLoading: false,
  error: null,
  offset: null,
  loaded: false,
  editNoteId: null,
};

@Injectable({ providedIn: 'root' })
export class VehicleNoteStore extends Store<VehicleNoteStoreState> {
  api = inject(VehicleNoteApiService);

  constructor() {
    super(initialState);
  }

  async loadNotes(
    vehicleId: number,
    params?: NoteParams,
  ): Promise<VehicleNote[]> {
    this.update({ listLoading: true, error: null });
    const [error, response] = await this.api.loadVehicleNotes(
      vehicleId,
      null,
      params,
    );
    if (error) {
      this.update({ listLoading: false, error });
      return;
    }
    this.update({
      notes: response.results,
      offset: response.next,
      listLoading: false,
      loaded: true,
    });
    return response.results;
  }

  async loadMore(
    vehicleId: number,
    params?: NoteParams,
  ): Promise<VehicleNote[]> {
    if (!this.state().offset || this.state().loadMoreLoading) return;
    this.update({ loadMoreLoading: true });
    const [error, response] = await this.api.loadVehicleNotes(
      vehicleId,
      this.state().offset || undefined,
      params,
    );
    if (error) {
      this.update({ loadMoreLoading: false, error });
      return;
    }
    this.draftUpdate((d) => {
      const existingIds = new Set(d.notes.map((n) => n.id));
      const uniqueResults = (response.results || []).filter(
        (n) => !existingIds.has(n.id),
      );
      d.notes = [...d.notes, ...uniqueResults];
      d.offset = response.next;
      d.loadMoreLoading = false;
    });
    return response.results;
  }

  async createNote(note: VehicleNoteForm): Promise<VehicleNote> {
    this.update({ loading: true, error: null });
    const [error, created] = await this.api.createVehicleNote(note);
    if (error) {
      this.update({ loading: false, error });
      throw error;
    }
    this.draftUpdate((d) => {
      d.notes = [created, ...d.notes];
      d.loading = false;
    });
    return created;
  }

  async updateNote(note: VehicleNoteForm): Promise<VehicleNote> {
    this.update({ loading: true, error: null });
    const [error, updated] = await this.api.updateVehicleNote(note);
    if (error) {
      this.update({ loading: false, error });
      throw error;
    }
    this.draftUpdate((d) => {
      const idx = d.notes.findIndex((n) => n.id === updated.id);
      if (idx !== -1) d.notes[idx] = updated;
      d.loading = false;
    });
    return updated;
  }

  async deleteNote(id: number): Promise<any> {
    this.update({ loading: true, error: null });
    const [error] = await this.api.deleteVehicleNote(id);
    if (error) {
      this.update({ loading: false, error });
      return error;
    }
    this.draftUpdate((d) => {
      d.notes = d.notes.filter((n) => n.id !== id);
      d.loading = false;
    });
  }

  async reloadNote(noteId: number): Promise<VehicleNote> {
    this.update({ loading: true, error: null });
    const [error, reloaded] = await this.api.getVehicleNote(noteId);
    if (error) {
      this.update({ loading: false, error });
      throw error;
    }
    this.draftUpdate((d) => {
      const idx = d.notes.findIndex((n) => n.id === reloaded.id);
      if (idx !== -1) d.notes[idx] = reloaded;
      d.loading = false;
    });
    return reloaded;
  }

  setEditNoteId(noteId: number | null) {
    this.update({ editNoteId: noteId });
  }

  clear() {
    this.update({ ...initialState });
  }
}
